import { PEER_CONNECTION_STATUS, ReceivedMessage, ProtocolArray, PROTOCOLS, AreaSystemRequest, SystemMessage, base64ToArrayBuffer, bufferToBase64, System } from "@gotti/core";
import { Client, ClientConfig } from "../Client.js";
import { ClientProcess } from "../ClientProcess.js";
import type { IClientSystem } from "../IClientSystem.js";
import { PlayerPeerConnection } from "../PlayerPeerConnection.js";

export type BaseClientConnectorConnectOptions = { 
    host: string,
    port: number,
    privateId: string, 
    publicId: string | number 
}

type PeerRawPayload = { to?: string | number; data: ArrayBuffer };

const encodePeerRawFrame = (payload: PeerRawPayload): ArrayBuffer => {
    const headerObj = payload.to !== undefined && payload.to !== null ? { to: payload.to } : undefined;
    const headerJson = headerObj ? JSON.stringify(headerObj) : '';
    const headerBytes = headerJson ? new TextEncoder().encode(headerJson) : new Uint8Array(0);
    const totalLength = 2 + headerBytes.length + payload.data.byteLength;
    const buffer = new ArrayBuffer(totalLength);
    const view = new DataView(buffer);
    view.setUint16(0, headerBytes.length, true);
    const result = new Uint8Array(buffer);
    result.set(headerBytes, 2);
    result.set(new Uint8Array(payload.data), 2 + headerBytes.length);
    return buffer;
};

const decodePeerRawFrame = (frame: ArrayBuffer): PeerRawPayload => {
    const view = new DataView(frame);
    const headerLength = Math.min(view.getUint16(0, true), frame.byteLength - 2);
    let to: string | number | undefined;
    let headerText = '';
    if (headerLength > 0) {
        const headerBytes = new Uint8Array(frame, 2, headerLength);
        headerText = new TextDecoder().decode(headerBytes);
        if (headerText) {
            try {
                const parsed = JSON.parse(headerText);
                to = parsed?.to;
            } catch (err) {
                console.warn('Failed to parse peer raw header', err);
            }
        }
    }
    const payloadData = frame.slice(2 + headerLength);
    return { to, data: payloadData };
};

export abstract class BaseClientConnector {
    private config : ClientConfig;
    private seq : number = 0;

    public connectorHost: string;
    public connectorPort: number;

    public peerConnections: {[ playerIndex: number]: PlayerPeerConnection } = {};
    private pendingPeerRequestCbs: {[playerIndex: number ]: (err?: any, options?: any) => void } = {};
    private peerAckRequestTimeouts: {[playerIndex: number]: any } = {};
    private peerConnectedRequestTimeouts: {[playerIndex: number]: any } = {};

    public publicId: string | number;
    public privateId: string;

    readonly process : ClientProcess

    readonly connectedPeerIds: Array<string | number> = [];

    readonly peerFailures :{[id: string]: Array<{ ts: number, reason: any, status: PEER_CONNECTION_STATUS }> } = {};

    readonly systemLookup : { [systemName: string ]: System & IClientSystem } = {}
    readonly requestCbs : {[requestId: string]: (responseData: any, error?: string) => void  } = {};
    readonly client : Client;

    /**
     * Assigned by `Client` (via `wireConnectionLost`) after this connector is
     * built. Concrete connectors invoke it when their underlying socket dies
     * unexpectedly (close/error), which drives `Client.handleConnectionLost`
     * (the auto-reconnect loop). Cleared FIRST in `disconnect()` so a
     * deliberate teardown never triggers a reconnect.
     */
    public onConnectionLost?: () => void;
    constructor(client: Client) {
      this.client = client;
      this.process = client.process;
      this.process.setConnector(this);
      this.systemLookup = this.process.systemLookup;
    }


    get id() : string | number {
      return this.publicId;
    }
    set id(_: string | number) {
      throw new Error(`Dont set id.`)
    }
    
    public async handleConnect<T>(options: BaseClientConnectorConnectOptions & T) : Promise<any> {
      const connected = await this.connect(options);
      this.connectorHost = options.host;
      this.connectorPort = options.port;
      return connected;
    }

    abstract connect<T>(options: BaseClientConnectorConnectOptions & T): Promise<any>
    abstract onDisconnect(): any;
    abstract send(data: ProtocolArray, ensureReliable?: boolean, ensureOrdered?: boolean): void

    /**
     * `areaId` addresses the frame at ONE area (the area file that started the
     * sending system — see areaRouting). Absent → the server fans it out to
     * every area the client is in, as it always did. Subclasses that bypass
     * this protocol envelope for the binary fast path frame the id into the
     * payload instead (`frameRawWithArea`), so both transports carry it.
     */
    public sendRaw(data: ArrayBuffer, ensureReliable = true, ensureOrdered = true, areaId?: string) {
        const encoded = bufferToBase64(data);
        const payload: ProtocolArray = [PROTOCOLS.CLIENT_TO_SERVER_RAW_MESSAGE, encoded];
        if (areaId !== undefined) payload.push(areaId);
        this.send(payload, ensureReliable, ensureOrdered);
    }


    public onServerMessage(msg: ProtocolArray) {
        const protocol = msg[0];
        // console.log('ON SERVER MSG', msg)
        switch(protocol) {
            case PROTOCOLS.MASTER_GLOBAL_BROADCAST: {
              const type = msg[1];
              const data = msg[2];
              this.process.systemArray.forEach((s: System & IClientSystem) => {
                s?.onMasterGlobalBroadcast({ type, data })
              });
              break;
            }
            case PROTOCOLS.MASTER_SYSTEM_MESSAGE: {
                const type = msg[1];
                const data = msg[2];
                const to = msg[3];
                this.process.systemLookup[to]?.onMasterSystemMessage?.({ type, data, })
                break;
            }
            case PROTOCOLS.SYSTEM_STATE_SYNC: {
                const areaId = msg[1];
                const systemName = msg[2];
                let nextState =  msg[3]; // either entire state object if msg.length < 5 or is state prop and value is at index 4)

                const system = this.process.systemLookup[systemName];
                if(!system) return;

                const previousState = system?.states[areaId];

                // means were just setting 1 prop on the state dont need to send whole object over code, hooks will work the same.
                if(msg.length === 5) {
                    const statePropValue = msg[4];
                    nextState = previousState ? {
                         ...previousState, 
                         [nextState]: statePropValue
                    } : {[nextState]: statePropValue }; // no previous state, just make the whole state object the [prop: value]
                }

                system.onAreaSystemStateUpdate?.(areaId, nextState, previousState);
                break;
            }
            case PROTOCOLS.CLIENT_TO_SERVER_REQUEST: {
              const seq = msg[1];
              const data = msg[2];
              const error = msg[3];
              if(this.requestCbs[seq]) {
                                  this.requestCbs[seq](data, error);
                                  delete this.requestCbs[seq];
              }
              break;
            }
            case PROTOCOLS.GAME_MESSAGE: {
              const type = msg[1];
              const data = msg[2];
              this.client.emitGameEvents(type, data);
              break;
            }
            case PROTOCOLS.SYSTEM_MESSAGE: {
							const type = msg[1];
							const data = msg[2];
							const to = msg[3];
							// The server may dispatch to a system the moment the client joins,
							// while startGame/initAreaFile is still awaiting preloads and the
							// system hasn't registered yet — deliverToSystem buffers that window
							// instead of silently dropping the message (players-invisible bug).
							const delivered = this.process.deliverToSystem(to, (sys: any) => sys.onServerMessage({ type, data }));
							if (!delivered) {
								console.warn(`[client] SYSTEM_MESSAGE for unknown system ${String(to)} dropped (type=${String(type)})`);
							}
							break;
            }
            case PROTOCOLS.PEER_CONNECTION_REQUEST_SUCCESS: {
							//console.log('PEER_CONNECTION_REQUEST_SUCCESS')
							const playerId = msg[1];
							//console.log('PEER_CONNECTION_REQUEST_SUCCESS', { playerId })
							const responseData = msg[2];
							if(this.peerConnections[playerId]) {
								this.peerConnections[playerId].peerHandshakeOptions = responseData; 
								this.peerConnections[playerId]?.checkAck();
							}
							break;
            }
            case PROTOCOLS.PEER_SIGNAL_REQUEST: {
							//console.log('PEER_SIGNAL_REQUEST')

							const data = msg[1];
							const playerId = data[0];
							//console.log('PEER_SIGNAL_REQUEST', { playerId })
							const signalData = data[1];
							this.peerConnections[playerId]?.checkAck();
							// console.log('peer signla data', { playerId, signalData });
							this.handleSignalData(playerId, signalData);
							break;
            }
            case PROTOCOLS.PEER_CONNECTION_REQUEST_FAILED:
                this.handlePeerFailure(msg[1], msg[2]);
                break;
            case PROTOCOLS.PEER_SIGNAL_FAILED: {
                this.handlePeerFailure(msg[1], msg[2]);
                break;
            }
            case PROTOCOLS.CONNECTOR_REQUEST: {
                const seq = msg[1];
                const protocol = msg[2];
                this.handleConnectorRequest(protocol, msg[3]).then(res => {
                    this.send([PROTOCOLS.CONNECTOR_REQUEST, seq, res], true, true);
                }).catch(err => {
                    const errmsg = (err && typeof err === "object" && "message" in err) 
                    ? String(err.message) 
                    : JSON.stringify(err, Object.getOwnPropertyNames(err)) || "Unknown error";
                    this.send([PROTOCOLS.CONNECTOR_REQUEST, seq, null, errmsg || JSON.stringify(err)], true, true);
                });
                break;
            }
            case PROTOCOLS.CLIENT_JOINED_AREA: {
                const areaId = msg[1];
                const areaType = msg[2];
                const options = msg[3];
                const areaData = msg[4];
                const areaState = msg[5];
                const leavesOtherAreas = msg[6]
                //TODO: no error handling on server atm. this will always be undefined or hang forever for now.
                const error = msg[7];
                this.client.handleJoinedArea(
                    areaId,
                    areaType,
                    areaData,
                    areaState, 
                    options, 
                    leavesOtherAreas,
                    error,
                );
                break;
              }
            case PROTOCOLS.CLIENT_LEFT_AREA: {
                const areaId = msg[1];
                const reason = msg[2];
                const options = msg[3];
                this.client.handleLeftArea(areaId, reason, options);
                break;
            }
            case PROTOCOLS.AREA_STATE_SYNC: {
                const areaId = msg[1];
                const state = msg[2];
                this.client.handleAreaStateSync(areaId, state);
                break;
            }
            case PROTOCOLS.GET_FIRST_AREAS: {
                const succeeded = msg[1];
                const payload = succeeded ? { areaIds: msg.slice(2) } : { error: msg[2] };
                this.client.emit('joined-first-areas',payload);
                break;
            }
            case PROTOCOLS.SERVER_TO_CLIENT_RAW_MESSAGE: {
                const encoded = msg[1];
                if (typeof encoded === 'string') {
                    const buffer = base64ToArrayBuffer(encoded);
                    this.client.handleRawAreaMessage(buffer);
                }
                break;
            }
            case PROTOCOLS.ASSET_CHANGED: {
              this.process.applyAssetChange(msg[1], msg[2]);
              break;
            }
            case PROTOCOLS.SERVER_REQUESTED_RECONNECT: {
                // The server is draining this connector for a graceful runtime
                // removal (Starter.removeConnectorAtRuntime -> ConnectorServer.
                // beginDrain) and wants us to re-gate onto a different one WHILE
                // still connected — not a socket death, so nothing else here
                // fires it. Route straight into the same driver an unexpected
                // close uses (Client.wireConnectionLost). handleConnectionLost's
                // loop tries its first attempt before any backoff sleep, so this
                // reconnect starts immediately.
                this.onConnectionLost?.();
                break;
            }
        }
    }
    private async handleServerToClientRequest(type: string | number, data: any, areaId: string | number, to?: number | string) {
        if(to === undefined || to === null) {
            return this.client.handleAreaRequest?.(areaId, { type, data })
        } else {
            return this.client.process.systemLookup[to].onAreaRequest(areaId, { type, data })
        }
    }

    public async handleConnectorRequest(protocol: PROTOCOLS, data: any) : Promise<any> {
        switch(protocol) {
            case PROTOCOLS.SERVER_TO_CLIENT_REQUEST:
                return this.handleServerToClientRequest(data[0], data[1], data[2], data[3]);
            case PROTOCOLS.PEER_CONNECTION_REQUEST:
                return this.handlePeerConnectionRequest(data[0], data[1], data[2], data[3]);
      
        }
    }

    public async joinArea(areaId: string, options?: any) {
      console.log('sending protocol request join area', areaId, options);
        this.send([PROTOCOLS.REQUEST_JOIN_AREA, areaId, options], true, true);
    }

    public async pingForFirstAreas(options?: any) {
        this.send([PROTOCOLS.GET_FIRST_AREAS, options], true, true);
    }

    public async requestServer(msg: AreaSystemRequest, timeout?: number) : Promise<any> {
        const seq = this.seq++;
        // OMIT the trailing `to` slot when absent (never send null/undefined over
        // the wire — bandwidth): a request with no `to` routes to the area FILE's
        // onClientGameRequest server-side, detected via `=== undefined`.
        const payload: ProtocolArray = [
            PROTOCOLS.CLIENT_TO_SERVER_REQUEST,
            seq,
            msg.areaId,
            msg.type,
            msg.data,
        ];
        if (msg.to !== undefined) payload.push(msg.to);
        this.send(payload, true, true);
        return new Promise((resolve, reject) => {
            this.requestCbs[seq] = (payload, error?: string) => {
                if(error) {
                    reject(new Error(error))
                } else {
                    resolve(payload)
                }
            }
            setTimeout(() => {
                return reject(`Timed out.`);
            }, timeout || this.config?.requestTimeout || 60000);
        });
     }

     private async handlePeerConnectionRequest(peerPublicId: string, signalData: { candidate?: any, sdp?: any }, systemName?: string, incomingRequestOptions?: any) : Promise<{ success: boolean, error?: string, response?: any }> {
      //  console.log('handlePeerConnectionRequest', { peerPublicId, systemName, incomingRequestOptions, signalData })
        try {
            //  console.log('handlePeerConnectionRequest', { peerPublicId, systemName, incomingRequestOptions, signalData })
            if(this.peerConnections[peerPublicId]) throw new Error(`Already had peer connection for peer ${peerPublicId}`);
            if(this.pendingPeerRequestCbs[peerPublicId]) throw new Error(`Already have pending peer request for peer ${peerPublicId}`);
            const directedToSystem = !(systemName === undefined || systemName === null || systemName === '')
           // console.log('directedToSystem?:', directedToSystem, systemName)
            const peerConnectionRequestHandler = directedToSystem ? 
                this.systemLookup[systemName]?.onPeerSystemConnectionRequest.bind(this.systemLookup[systemName]) :
                this.client.handlePeerConnectionRequest.bind(this.client);

            this.peerConnections[peerPublicId] = new PlayerPeerConnection(this, this.publicId, peerPublicId, systemName);
            const response = await peerConnectionRequestHandler?.(peerPublicId, incomingRequestOptions)
            // console.log('peerConnectionRequestHandler response', { response, peerPublicId, incomingRequestOptions })
            if(!response) { // our system requester invalidated the connection.
              delete this.peerConnections[peerPublicId];  
              throw new Error('System failed to accept peer system request.')
            }
            this.setupPeerConnection(this.peerConnections[peerPublicId], peerPublicId);
            this.peerConnections[peerPublicId].peerHandshakeOptions = incomingRequestOptions;
            this.peerConnections[peerPublicId].acceptConnection(response);
            // do this in setTimeout so we can return the response for server early.
            setTimeout(() => this.handleSignalData(peerPublicId, signalData))
            return { success: true, response }
        } catch (err: any) {
            // ensure we cleanup any pre-created placeholder on failure so future requests are not blocked
            if (this.peerConnections[peerPublicId]) {
              try { this.peerConnections[peerPublicId].destroy(); } catch {}
              delete this.peerConnections[peerPublicId];
            }
            return { success: false, error: err?.message || String(err) }
        }
    }

    public sendToPeer(peerId: string | number, message: SystemMessage) {
			this.peerConnections[peerId].send(JSON.stringify([message.type, message.data, message.to,]))
    }

    public sendToPeers(peerIds: (string | number)[], message: SystemMessage) {
			const payload = JSON.stringify([message.type, message.data, message.to,]);
			for(let i = 0; i < peerIds.length; i++) {
				this.peerConnections[peerIds[i]].send(payload)
			}
    }

    public sendPeerRaw(peerId: string | number, data: ArrayBuffer, to?: string | number) {
        this.peerConnections[peerId].send(encodePeerRawFrame({ data, to }));
    }

    public sendPeersRaw(peerIds: (string | number)[], data: ArrayBuffer, to?: string | number) {
        for(let i = 0; i < peerIds.length; i++) {
            this.sendPeerRaw(peerIds[i], data, to);
        }
    }

    public sendToAllPeers(message: SystemMessage) {
			this.sendToPeers(this.connectedPeerIds, message)
    }

    private handleSignalData(peerPublicId, signalData) {
			const peerConnection = this.peerConnections[peerPublicId];
			if(!peerConnection) {
				console.error('Attempted to handle signal data for non existent peer:', peerPublicId);
				return;
			}

			if(signalData.sdp) {
				peerConnection.handleSDPSignal(signalData.sdp)
			} else if (signalData.candidate) {
				peerConnection.handleIceCandidateSignal(signalData.candidate)
			}
    }

    public requestPeerConnection(playerPublicId: string | number, systemName: string | number, requestOptions: Record<string, any>, systemRequestCallback: (err?: string, data?: any) => void, ackTimeout?: number, connectionTimeout?: number) {
			ackTimeout = ackTimeout ? ackTimeout : 3000;
			connectionTimeout = connectionTimeout ? connectionTimeout : 5000;
			let peerConnection = this.peerConnections[playerPublicId];
			if(!(peerConnection)) {
				peerConnection = new PlayerPeerConnection(
					this,
					this.publicId,
					playerPublicId,
					systemName, 
					null,
					PEER_CONNECTION_STATUS.WAITING_FOR_INITIAL_RESPONSE,
				);
				this.setupPeerConnection(peerConnection, playerPublicId);
				requestOptions = {
					...(requestOptions || {}),
					systemName,
				}
				peerConnection.requestConnection(requestOptions);
				this.peerConnections[playerPublicId] = peerConnection;
				this.pendingPeerRequestCbs[playerPublicId] = (err, data) => {
					systemRequestCallback(err, data)
					delete this.pendingPeerRequestCbs[playerPublicId];
				};

				this.peerAckRequestTimeouts[playerPublicId] = setTimeout(() => {
					peerConnection.clearAckListeners();
					delete this.peerAckRequestTimeouts[playerPublicId];
					this.handlePeerFailure(playerPublicId, 'Peer connection timed out, never received an acknowledgement back from peer.');
				}, ackTimeout);

				// add the ack callback to remove the timeout
				peerConnection.onceAcked(() => {
					clearTimeout(this.peerAckRequestTimeouts[playerPublicId]);
					delete this.peerAckRequestTimeouts[playerPublicId];
					peerConnection.status = PEER_CONNECTION_STATUS.WAITING_FOR_HANDSHAKE;
     
					// we got the ack, now we want to add a callback for the actual request unless for some reason the
					// peer connection was already connected
					if(!peerConnection.connected) {
						this.peerConnectedRequestTimeouts[playerPublicId] = setTimeout(() => {
							delete this.peerConnectedRequestTimeouts[playerPublicId];
							this.handlePeerFailure(playerPublicId, 'Peer connection timed out on actual handshake of webRTC connection');
						}, connectionTimeout);
					}
				});
			} else {
				throw new Error(`Already existing peer connection for peer:, ${playerPublicId}`);
			}
    }

    public stopAllPeerConnections() {
			for(const peerPublicId in this.peerConnections) {
				this.peerConnections[peerPublicId].destroy();
			}
    }

    public stopPeerConnection(peerPublicId: string | number) {
			if (this.peerConnections[peerPublicId]) {
				this.peerConnections[peerPublicId].destroy();
			}
    }

    /**
     * Best-effort flush of buffered sends before a DELIBERATE teardown, so a
     * final frame (e.g. PROTOCOLS.CLIENT_LEAVING sent by Client.close()) is
     * actually transmitted before the transport is destroyed. Default: no-op —
     * websocket's close() already flushes queued data before the close
     * handshake, and there is nothing to drain on the mock. WebRTC overrides
     * this because its destroy() (peerConnection.close()) would otherwise abort
     * the SCTP transport with the reliable frame still buffered, silently
     * dropping the deliberate-leave signal → the server falls back to the grace
     * path. Must be internally capped so it can never hang close().
     */
    public async flushBeforeLeave(): Promise<void> {}

    public async disconnect() {
        // Clear FIRST: this is a DELIBERATE teardown, so the socket 'close' the
        // onDisconnect() below triggers must not fire the reconnect driver. This
        // also covers close()/clearGame(), which route through disconnect().
        this.onConnectionLost = undefined;
        this.removeAllListeners();
        this.stopAllPeerConnections();
        await this.onDisconnect();
    }

    public sendClientPeerMessage(peerPublicId, message: ReceivedMessage) {
        this.peerConnections[peerPublicId].send(JSON.stringify([message.type, message.data, undefined]))
    }

    public sendClientAllPeersMessage(message: ReceivedMessage) {
        const len = this.connectedPeerIds.length;
        const encoded = JSON.stringify([message.type, message.data, undefined]);
        for(let i = 0; i < len; i++) {
            this.peerConnections[this.connectedPeerIds[i]].send(encoded)
        }
    }

    public sendClientPeersMessage(peerPublicIdes: Array<number | string>, message: ReceivedMessage) {
        const len = peerPublicIdes.length;
        const encoded = JSON.stringify([message.type, message.data, undefined]);
        for(let i = 0; i < len; i++) {
            this.peerConnections[peerPublicIdes[i]].send(encoded)
        }
    }

    public sendPeerMessage(peerPublicId: string | number, message: SystemMessage) {
        this.peerConnections[peerPublicId].send(JSON.stringify([message.type, message.data, message.to,]))
    }

    public sendAllPeersMessage(message: SystemMessage) {
        const len = this.connectedPeerIds.length;
        const encoded = JSON.stringify([message.type, message.data, message.to,]);
        for(let i = 0; i < len; i++) {
            this.peerConnections[this.connectedPeerIds[i]].send(encoded)
        }
    }

    public sendPeersMessage(peerPublicIdes: Array<number | string>, message: SystemMessage) {
        const len = peerPublicIdes.length;
        const encoded = JSON.stringify([message.type, message.data, message.to,]);
        for(let i = 0; i < len; i++) {
            this.peerConnections[peerPublicIdes[i]].send(encoded)
        }
    }

    public get hasJoined() {
        return this.privateId !== undefined;
    }

    public removeAllListeners() {
        this.pendingPeerRequestCbs = {};
        this.peerAckRequestTimeouts = {};
        this.peerConnectedRequestTimeouts = {};
    }

    private handlePeerFailure(peerPublicId, err?: string | boolean) {
        const peerConnection = this.peerConnections[peerPublicId];
        if(!peerConnection) {
            console.error(`Peer failure for peerPublicId ${peerPublicId} did not have a correlated peer connection.`);
        } else {
            peerConnection.offConnected();
            if(this.client.config.failedPeerRequestTrackerTimeout) {
                if(!(peerPublicId in this.peerFailures)) {
                    this.peerFailures[peerPublicId] = [];
                }
                const failure = { ts: Date.now(), reason: err || 'unknown', status: peerConnection.status };
                this.peerFailures[peerPublicId].push(failure);
                setTimeout(() => {
                    if(this.peerFailures[peerPublicId]) {
                        const idx = this.peerFailures[peerPublicId].indexOf(failure);
                        if(idx > -1) {
                            this.peerFailures[peerPublicId].splice(idx, 1);
                            if(!this.peerFailures[peerPublicId].length) {
                                delete this.peerFailures[peerPublicId];
                            }
                        }
                    }
               }, this.client.config.failedPeerRequestTrackerTimeout)
            }
            peerConnection.status = PEER_CONNECTION_STATUS.FAILED;
        }

        // check to see if we have a pending request so we can initiate the callback
        this.pendingPeerRequestCbs[peerPublicId]?.(err)
        if(this.peerAckRequestTimeouts[peerPublicId]) {
            clearTimeout(this.peerAckRequestTimeouts[peerPublicId]);
            delete this.peerAckRequestTimeouts[peerPublicId];
        }
        if (this.peerConnectedRequestTimeouts[peerPublicId]) {
            clearTimeout(this.peerConnectedRequestTimeouts[peerPublicId]);
            delete this.peerConnectedRequestTimeouts[peerPublicId]
        }

        const index = this.connectedPeerIds.indexOf(peerPublicId);
        if(index > -1) {
            this.connectedPeerIds.splice(index, 1);

            this.process.systemArray.forEach((s: System & IClientSystem) => {
                s.onAnyPeerConnectionEnded?.(peerPublicId)
            });
            if(peerConnection) {
                const system : System & IClientSystem = this.process.systemLookup[peerConnection.peerSystem as unknown as string] as System & IClientSystem;
                system?.onPeerSystemConnectionEnded?.(peerPublicId)
            }
            this.client.handlePeerConnectionEnded?.(peerPublicId)
        }
        delete this.peerConnections[peerPublicId];
        peerConnection?.destroy();
    }

    private setupPeerConnection(peerConnection: PlayerPeerConnection, peerPublicId) {
        peerConnection.onConnected((options) => {
            if(!this.connectedPeerIds.includes(peerPublicId)) {
                this.pendingPeerRequestCbs[peerPublicId]?.(null, options);
                // check ack for good measure ;]
                if(this.peerAckRequestTimeouts[peerPublicId]) {
                    clearTimeout(this.peerAckRequestTimeouts[peerPublicId]);
                    delete this.peerAckRequestTimeouts[peerPublicId];
                }
                // this should still be defined..
                if(this.peerConnectedRequestTimeouts[peerPublicId]) {
                    clearTimeout(this.peerConnectedRequestTimeouts[peerPublicId]);
                    delete this.peerConnectedRequestTimeouts[peerPublicId];
                }
                this.connectedPeerIds.push(peerPublicId);
             
                const system : System & IClientSystem = this.process.systemLookup[peerConnection.peerSystem as unknown as string] as System & IClientSystem;
                system?.onPeerSystemConnectionStarted?.(peerPublicId, options)

                this.process.systemArray.forEach((s: System & IClientSystem) => {
                    s.onAnyPeerConnectionStarted?.(peerPublicId, options)
                });
                this.client.handlePeerConnectionStarted?.(peerPublicId, options);
            }
        });

        peerConnection.onDisconnected(reason => {
            this.handlePeerFailure(peerPublicId, reason)
        });
    
        peerConnection.onMessage((data: SystemMessage | ReceivedMessage) => {
            if((data as SystemMessage).to !== undefined) {
                const system = this.process.systemLookup[(data as SystemMessage).to as unknown as string] as System & IClientSystem;
                system?.onPeerSystemMessage?.(peerPublicId, data);
                return;
            }
            this.client.handlePeerMessage?.(peerPublicId, data);
        });

        peerConnection.onRawMessage((frame) => {
            this.handlePeerRawFrame(peerPublicId, frame);
        });

        peerConnection.onMissedPing((concurrentMissedPings: number) => {
            this.process.systemArray.forEach((s: System & IClientSystem) => {
                s.onAnyPeerConnectionMissedPing?.(peerPublicId, concurrentMissedPings)
            });
        });
    }

    private handlePeerRawFrame(peerPublicId: string | number, frame: ArrayBuffer) {
        const { to, data } = decodePeerRawFrame(frame);
        const targetKey = to !== undefined && to !== null ? String(to) : null;
        const targetSystem = targetKey ? this.process.systemLookup[targetKey] as System & IClientSystem : undefined;
        if(targetSystem?.onRawPeerMessage) {
            targetSystem.onRawPeerMessage(peerPublicId, data, to);
        }
        this.process.rawPeerMessageSystems.forEach(system => {
            if(system === targetSystem) {
                return;
            }
            system.onRawPeerMessage?.(peerPublicId, data, to);
        });
    }
}